// Cache configuration by data type
export const CACHE_CONFIG = {
  // User data - rarely changes
  'user-profile': { ttl: 7 * 24 * 60 * 60 * 1000 },      // 7 days
  'restaurant-settings': { ttl: 24 * 60 * 60 * 1000 },   // 24 hours
  'onboarding': { ttl: 24 * 60 * 60 * 1000 },            // 24 hours
  
  // Business data - medium frequency changes
  'ingredients': { ttl: 5 * 60 * 1000 },                 // 5 minutes
  'menu-items': { ttl: 5 * 60 * 1000 },                  // 5 minutes
  'recipes': { ttl: 5 * 60 * 1000 },                     // 5 minutes
  'combos': { ttl: 5 * 60 * 1000 },                      // 5 minutes
  'categories': { ttl: 30 * 60 * 1000 },                 // 30 minutes
  
  // Dynamic data - changes frequently
  'sales': { ttl: 60 * 1000 },                           // 1 minute
  'dashboard': { ttl: 60 * 1000 },                       // 1 minute
  'goals': { ttl: 5 * 60 * 1000 },                       // 5 minutes
  
  // Configuration data
  'taxes': { ttl: 30 * 60 * 1000 },                      // 30 minutes
  'service-fees': { ttl: 30 * 60 * 1000 },               // 30 minutes
  'payment-fees': { ttl: 30 * 60 * 1000 },               // 30 minutes
  
  // Images - rarely change
  'service-type-images': { ttl: 30 * 24 * 60 * 60 * 1000 }, // 30 days
  'product-images': { ttl: 7 * 24 * 60 * 60 * 1000 },       // 7 days
  'profile-images': { ttl: 7 * 24 * 60 * 60 * 1000 },       // 7 days
} as const;

// Centralized staleTime constants for TanStack Query
// Use these in useQuery({ staleTime: QUERY_STALE_TIMES['key'] })
// OPTIMIZED: Increased TTLs to reduce server load and improve capacity
export const QUERY_STALE_TIMES = {
  // Data that rarely changes - longer stale times (realtime handles invalidation)
  'user-profile': 30 * 60 * 1000,        // 30 minutes - rarely changes
  'restaurant-settings': 20 * 60 * 1000, // 20 minutes - configuration
  'organization': 30 * 60 * 1000,        // 30 minutes - rarely changes
  'user-stores': 20 * 60 * 1000,         // 20 minutes - rarely changes
  'restaurant-config': 20 * 60 * 1000,   // 20 minutes - configuration
  
  // Moderate frequency changes - realtime invalidation handles freshness
  'subscription': 5 * 60 * 1000,         // 5 minutes
  'referrals': 10 * 60 * 1000,           // 10 minutes
  'ingredients': 10 * 60 * 1000,         // 10 minutes - realtime updates
  'menu-items': 10 * 60 * 1000,          // 10 minutes - realtime updates
  'recipes': 10 * 60 * 1000,             // 10 minutes - realtime updates
  'combos': 10 * 60 * 1000,              // 10 minutes - realtime updates
  'categories': 2 * 60 * 60 * 1000,      // 2 hours - almost never changes
  
  // Cost-related data - static config, realtime invalidates on change
  'taxes': 2 * 60 * 60 * 1000,           // 2 hours - static config
  'service-fees': 2 * 60 * 60 * 1000,    // 2 hours - static config
  'payment-fees': 2 * 60 * 60 * 1000,    // 2 hours - static config
  'fixed-costs': 2 * 60 * 60 * 1000,     // 2 hours - static config
  
  // High-frequency data - realtime updates handle freshness
  'sales': 60 * 1000,                    // 1 minute (was 30s) - realtime updates
  'dashboard': 2 * 60 * 1000,            // 2 minutes (was 1)
  'dashboard-kpis': 2 * 60 * 1000,       // 2 minutes - materialized view
  'goals': 10 * 60 * 1000,               // 10 minutes (was 5)
  'stock': 2 * 60 * 1000,                // 2 minutes (was 1) - realtime updates
  
  // Production data
  'pending-productions': 2 * 60 * 1000,  // 2 minutes
  'production-history': 5 * 60 * 1000,   // 5 minutes
  
  // Support dashboard data (less frequent updates)
  'support-data': 5 * 60 * 1000,         // 5 minutes
  'pending-users': 2 * 60 * 1000,        // 2 minutes (critical for approvals)
  'support-tickets': 2 * 60 * 1000,      // 2 minutes
  'support-clients': 5 * 60 * 1000,      // 5 minutes
  'support-referrals': 5 * 60 * 1000,    // 5 minutes
  'support-audit-logs': 5 * 60 * 1000,   // 5 minutes
  
  // Stock movements and history
  'stock-movements': 5 * 60 * 1000,      // 5 minutes
  'stock-movements-today': 2 * 60 * 1000, // 2 minutes
} as const;

export type QueryStaleTimeKey = keyof typeof QUERY_STALE_TIMES;

export type CacheType = keyof typeof CACHE_CONFIG;

// Limits
export const MAX_CACHE_SIZE = 50 * 1024 * 1024; // 50MB
export const CLEANUP_INTERVAL = 60 * 60 * 1000;  // 1 hour
export const INACTIVITY_THRESHOLD = 7 * 24 * 60 * 60 * 1000; // 7 days

// App version for cache busting - Increment to force IndexedDB cleanup on all clients
// v1.6.0: Fixed sales crash with undefined queryKey validation in idbPersister
// v1.7.0: Fixed race condition - synchronous cache cleanup before React mounts
// v1.8.0: Fixed async race condition - await IndexedDB deletion before React mounts
// v1.9.0: Added Array.isArray + deep object validation for summary/sales during hydration
// v2.0.0: Context Guard Barrier pattern - hooks only called after RestaurantContext is ready
// v2.1.0: Complete barrier in QuickSaleForm + stable queryKey in useUnifiedSales when disabled
// v2.2.0: Removed dead QuickSaleForm.tsx, toggle Online/Offline implemented in SaleRegistrationForm
// v2.3.0: Master performance optimization - staleTimes, React.memo, realtime expansion, prefetch
export const APP_VERSION = '2.3.0';

interface CacheEntry<T = unknown> {
  data: T;
  timestamp: number;
  type: CacheType;
  size: number;
}

// In-memory cache
const memoryCache = new Map<string, CacheEntry>();

// Get TTL for a cache type
export function getTTL(type: CacheType): number {
  return CACHE_CONFIG[type]?.ttl || 5 * 60 * 1000; // Default 5 minutes
}

// Estimate size of data in bytes
function estimateSize(data: unknown): number {
  try {
    return new Blob([JSON.stringify(data)]).size;
  } catch {
    return 0;
  }
}

// Set cache entry
export function setCache<T>(key: string, data: T, type: CacheType): void {
  const size = estimateSize(data);
  const entry: CacheEntry<T> = {
    data,
    timestamp: Date.now(),
    type,
    size,
  };
  
  memoryCache.set(key, entry as CacheEntry);
  
  // Check if we need to cleanup
  if (getCacheSize() > MAX_CACHE_SIZE) {
    cleanupOldestEntries();
  }
}

// Get cache entry if valid
export function getCache<T>(key: string): T | null {
  const entry = memoryCache.get(key) as CacheEntry<T> | undefined;
  
  if (!entry) return null;
  
  const ttl = getTTL(entry.type);
  const isExpired = Date.now() - entry.timestamp > ttl;
  
  if (isExpired) {
    memoryCache.delete(key);
    return null;
  }
  
  return entry.data;
}

// Check if cache entry exists and is valid
export function hasValidCache(key: string): boolean {
  return getCache(key) !== null;
}

// Invalidate cache by key or pattern
export function invalidateCache(pattern: string | RegExp): void {
  const regex = typeof pattern === 'string' ? new RegExp(pattern) : pattern;
  
  for (const key of memoryCache.keys()) {
    if (regex.test(key)) {
      memoryCache.delete(key);
    }
  }
}

// Invalidate all cache for a specific type
export function invalidateCacheType(type: CacheType): void {
  for (const [key, entry] of memoryCache.entries()) {
    if (entry.type === type) {
      memoryCache.delete(key);
    }
  }
}

// Get total cache size in bytes
export function getCacheSize(): number {
  let total = 0;
  for (const entry of memoryCache.values()) {
    total += entry.size;
  }
  return total;
}

// Get cache stats
export function getCacheStats() {
  const stats = {
    totalSize: getCacheSize(),
    totalEntries: memoryCache.size,
    byType: {} as Record<string, { count: number; size: number }>,
  };
  
  for (const entry of memoryCache.values()) {
    if (!stats.byType[entry.type]) {
      stats.byType[entry.type] = { count: 0, size: 0 };
    }
    stats.byType[entry.type].count++;
    stats.byType[entry.type].size += entry.size;
  }
  
  return stats;
}

// Cleanup expired entries
export function cleanupExpiredCache(): number {
  let cleaned = 0;
  const now = Date.now();
  
  for (const [key, entry] of memoryCache.entries()) {
    const ttl = getTTL(entry.type);
    if (now - entry.timestamp > ttl) {
      memoryCache.delete(key);
      cleaned++;
    }
  }
  
  return cleaned;
}

// Cleanup oldest entries (LRU) until under size limit
function cleanupOldestEntries(): void {
  const entries = Array.from(memoryCache.entries())
    .sort((a, b) => a[1].timestamp - b[1].timestamp);
  
  let currentSize = getCacheSize();
  const targetSize = MAX_CACHE_SIZE * 0.8; // Clean to 80% of max
  
  for (const [key] of entries) {
    if (currentSize <= targetSize) break;
    const entry = memoryCache.get(key);
    if (entry) {
      currentSize -= entry.size;
      memoryCache.delete(key);
    }
  }
}

// Clear all cache
export function clearAllCache(): void {
  memoryCache.clear();
}

// Format bytes for display
export function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}
